import { isDefined } from "@clipboard-health/util-ts";
import { Stack } from "@mui/material";
import { APP_V2_USER_EVENTS, logEvent } from "@src/appV2/lib/analytics";
import { useGeofenceContext } from "@src/appV2/Location/geofence/useGeofence";
import { calculateMinutesElapsed } from "@src/appV2/redesign/ArrivalTime/calculateMinutesElapsed";
import { HourlyPayLabel } from "@src/appV2/redesign/Pay/HourlyPayLabel";
import { TotalPayLabel } from "@src/appV2/redesign/Pay/TotalPayLabel";
import { getFormattedShiftPayBreakdown } from "@src/appV2/redesign/Shift/getFormattedShiftPayBreakdown";
import { InstantPayIndicator } from "@src/appV2/redesign/WorkerShifts/components/InstantPayIndicator";
import { useWorkplaceGuide } from "@src/appV2/redesign/Workplace/useWorkplaceGuide";
import { type ShiftStateData } from "@src/appV2/Shifts/Shift/ShiftState/types";
import type { WorkerShift } from "@src/appV2/Shifts/Shift/types";
import { differenceInMinutes, parseISO } from "date-fns";

import { Button } from "../../../components/Button";
import { StickyFooter } from "../../../components/StickyFooter";
import { TextButton } from "../../../components/TextButton";
import {
  ALREADY_STARTED_WORKING_THRESHOLD_MINUTES,
  SHIFT_DETAILS_FOOTER_LOG_EVENT_SOURCE_UI,
} from "../constants";
import {
  getClockInVisibility,
  useClockInVisibilityTracking,
} from "../hooks/useClockInVisibilityTracking";
import { useFooterPrimaryAction } from "../hooks/useShiftActionVisibility";
import { getClockInButtonText } from "../utils/getClockInButtonText";
import { CancelShiftButton } from "./CancelShiftButton";

export interface InstantPayFooterProps {
  shift: WorkerShift;
  shiftState: ShiftStateData;
  currentDate: Date;
  isBadgeVerificationEnabled: boolean;
  onClockIn: () => void;
  onUpdateETA: () => void;
  onCancelShift: () => void;
  onTransferShift: () => void;
  onSentHome: () => void;
  onAlreadyStartedWorking: () => void;
}

function InstantPayFooter(props: InstantPayFooterProps) {
  const {
    shift,
    shiftState,
    currentDate,
    isBadgeVerificationEnabled,
    onClockIn,
    onUpdateETA,
    onCancelShift,
    onTransferShift,
    onSentHome,
    onAlreadyStartedWorking,
  } = props;

  const { primaryAction, showCancelShift } = useFooterPrimaryAction({
    shift,
    shiftState,
    currentDate,
  });

  const { inside } = useGeofenceContext();
  const { state: visibilityState, reason: visibilityReason } = getClockInVisibility(
    primaryAction,
    inside
  );
  useClockInVisibilityTracking({
    state: visibilityState,
    reason: visibilityReason,
    shiftId: shift._id,
    workerId: shift.agentId,
    workplaceId: shift.facilityId,
  });

  const shiftStartDate = isDefined(shift.start) ? parseISO(shift.start) : undefined;

  const hourlyPay = shift.offer?.pay.value ?? shift.finalPay ?? 0;
  const minutesElapsed = isDefined(shiftStartDate)
    ? calculateMinutesElapsed(shiftStartDate, currentDate)
    : 0;

  const shiftLengthInHours = differenceInMinutes(parseISO(shift.end), parseISO(shift.start)) / 60;

  const adjustedOriginalAmount = isDefined(shift.originalAmount)
    ? Math.max(0, shift.originalAmount - (minutesElapsed / 60) * hourlyPay)
    : undefined;

  const payBreakdown = getFormattedShiftPayBreakdown(
    shift.offer?.pay.value,
    shift.finalPay,
    adjustedOriginalAmount,
    undefined,
    shiftLengthInHours,
    shift.facility.requiresLunchBreak
  );

  // Prefetch clock in guide so that the button state is ready when someone opens the bottom sheet
  useWorkplaceGuide({ workplaceId: shift.facilityId, category: "clock_in" });

  const { isMissedPunchRequestEnabledV2 } = shiftState.metadata;

  const showAlreadyStartedWorking = (() => {
    if (!isMissedPunchRequestEnabledV2 || !isDefined(shiftStartDate)) {
      return false;
    }

    const minutesSinceShiftStart = differenceInMinutes(currentDate, shiftStartDate);
    if (minutesSinceShiftStart < ALREADY_STARTED_WORKING_THRESHOLD_MINUTES) {
      return false;
    }

    const earliestPlausibleArrivalAt = shift.attendance?.earliestPlausibleArrivalAt;
    if (isDefined(earliestPlausibleArrivalAt)) {
      const epat = parseISO(earliestPlausibleArrivalAt);
      const minutesSinceEpat = differenceInMinutes(currentDate, epat);
      if (minutesSinceEpat < ALREADY_STARTED_WORKING_THRESHOLD_MINUTES) {
        return false;
      }
    }

    return true;
  })();

  const renderPrimaryButton = () => {
    if (!primaryAction) {
      return null;
    }

    const { action, enabled } = primaryAction;

    // FIXME: Automatically disabled during ESLint config update
    switch (action) {
      case "transferShift": {
        return (
          <Button
            fullWidth
            variant="outlined"
            size="large"
            disabled={!enabled}
            onClick={() => {
              logEvent(APP_V2_USER_EVENTS.SHIFT_DETAILS_TRANSFER_SHIFT_TAPPED, {
                shiftId: shift._id,
                sourceUI: SHIFT_DETAILS_FOOTER_LOG_EVENT_SOURCE_UI,
              });
              onTransferShift();
            }}
          >
            Transfer shift
          </Button>
        );
      }

      case "clockIn": {
        return (
          <Button
            fullWidth
            size="large"
            startIconType={enabled ? undefined : "locked"}
            // Not using `disabled` prop — onClick must fire to show the "too early" bottom sheet
            sx={
              enabled
                ? undefined
                : (theme) => ({
                    backgroundColor: theme.palette.intent?.disabled.background,
                    color: theme.palette.intent?.disabled.text,
                    "&:hover": {
                      backgroundColor: theme.palette.intent?.disabled.background,
                    },
                  })
            }
            onClick={() => {
              logEvent(APP_V2_USER_EVENTS.SHIFT_DETAILS_CLOCK_IN_NOW_TAPPED, {
                shiftId: shift._id,
                sourceUI: SHIFT_DETAILS_FOOTER_LOG_EVENT_SOURCE_UI,
              });
              onClockIn();
            }}
          >
            {getClockInButtonText(isBadgeVerificationEnabled)}
          </Button>
        );
      }

      case "updateEta": {
        return (
          <Button
            fullWidth
            size="large"
            onClick={() => {
              logEvent(APP_V2_USER_EVENTS.SHIFT_DETAILS_LATE_FOR_SHIFT_TAPPED, {
                shiftId: shift._id,
                sourceUI: SHIFT_DETAILS_FOOTER_LOG_EVENT_SOURCE_UI,
              });
              onUpdateETA();
            }}
          >
            Update ETA
          </Button>
        );
      }

      case "sentHome": {
        return (
          <Button
            fullWidth
            size="large"
            onClick={() => {
              logEvent(APP_V2_USER_EVENTS.SHIFT_DETAILS_SENT_HOME_TAPPED, {
                shiftId: shift._id,
                sourceUI: SHIFT_DETAILS_FOOTER_LOG_EVENT_SOURCE_UI,
              });
              onSentHome();
            }}
          >
            I was sent home
          </Button>
        );
      }

      default: {
        return null;
      }
    }
  };

  return (
    <StickyFooter>
      <Stack
        direction="row"
        justifyContent="space-between"
        alignItems="center"
        sx={{ paddingX: 2, marginBottom: 5 }}
      >
        <HourlyPayLabel size="large" hourlyPay={payBreakdown?.hourlyPay} />
        <Stack direction="row" spacing={2} alignItems="center">
          <TotalPayLabel
            totalPay={payBreakdown?.workedBreakPay?.totalPay ?? payBreakdown?.totalPay}
          />
          {isDefined(shift) && shift.isInstantPay && <InstantPayIndicator />}
        </Stack>
      </Stack>

      <Stack spacing={3}>
        {renderPrimaryButton()}
        {showCancelShift && <CancelShiftButton shiftId={shift._id} onCancelShift={onCancelShift} />}
        {showAlreadyStartedWorking && primaryAction?.action === "clockIn" && (
          <TextButton
            underline
            size="large"
            sx={{ width: "100%", height: "auto", paddingY: 4 }}
            onClick={() => {
              logEvent(APP_V2_USER_EVENTS.SHIFT_DETAILS_ALREADY_STARTED_WORKING_TAPPED, {
                shiftId: shift._id,
                sourceUI: SHIFT_DETAILS_FOOTER_LOG_EVENT_SOURCE_UI,
              });
              onAlreadyStartedWorking();
            }}
          >
            I already started working
          </TextButton>
        )}
      </Stack>
    </StickyFooter>
  );
}

export { InstantPayFooter };
